import { createContext, useContext, Children } from "react";
import colors from "@/config/colors";
import {
  formatMoney,
  getEconomyDisplayName,
  formatPercentage,
} from "@/utils/format";
import styles from "./styles.module.scss";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { positionY } from "../utils";
import type { Placement } from "../utils";

const TooltipContext = createContext<{
  placement: Placement;
  numItems: number;
  width: number;
}>({
  placement: [],
  numItems: 0,
  width: 0,
});

interface RootProps {
  x: number;
  year: number;
  bottom: number;
  top?: number;
  containerX: number;
}

function Root({
  year,
  x,
  bottom,
  top,
  containerX,
  children,
}: React.PropsWithChildren<RootProps>) {
  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is600px = useMediaQuery("(min-width: 600px)");
  const width = is1024px ? 180 : is600px ? 125 : 100;

  // Make sure the tooltip isn't overflowing the screen
  const adjustedX =
    x + containerX + width >= window.innerWidth
      ? window.innerWidth - containerX - width - x
      : 0;

  return (
    <div
      className={styles["tooltip-container"]}
      style={{
        top,
        left: x,
        bottom,
      }}
    >
      <div className={styles["tooltip-line"]}>
        <p className={styles["tooltip-year-label"]}>
          <span>{year}</span>
        </p>
      </div>
      <TooltipContext.Provider
        value={{ placement: [], numItems: Children.count(children), width }}
      >
        <div className={styles["tooltip-content"]} style={{ left: adjustedX }}>
          {children}
        </div>
      </TooltipContext.Provider>
    </div>
  );
}

interface ItemProps {
  colorName: keyof typeof colors;
  economyCode: string;
  value?: number;
  isPercent?: boolean;
  y: number;
}

function Item({ colorName, economyCode, value, isPercent, y }: ItemProps) {
  const { placement, numItems, width } = useContext(TooltipContext);

  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is600px = useMediaQuery("(min-width: 600px)");

  // Set heights for tooltip postions based on viewport size
  const height = is1024px ? 26 : is600px ? 20 : 16;

  const tooltipY = positionY({
    // Make sure tooltip doesn't overlap year label
    y: Math.max(height / 2 + 2, y),
    height,
    economyCode,
    placement,
  });

  if (placement.length >= numItems) {
    while (placement.length > 0) {
      placement.pop();
    }
  }

  return (
    <div
      style={{
        top: tooltipY,
        left: 0,
        width: width,
        height: height,
      }}
      className={styles.tooltip}
    >
      <div className={styles["tooltip-left"]}>
        <div
          className={styles["tooltip-dot"]}
          style={{
            backgroundColor: colors[colorName],
          }}
        />
        <p
          className={styles["tooltip-label"]}
          style={{
            width: is1024px ? width - 75 : is600px ? width - 70 : width - 55,
          }}
        >
          {getEconomyDisplayName(economyCode)}
        </p>
      </div>
      <p className={styles["tooltip-value"]}>
        {!value
          ? "—"
          : isPercent
          ? formatPercentage(value / 100)
          : formatMoney(value)}
      </p>
    </div>
  );
}

export { Root, Item };
